// @ts-nocheck
import { useState, useEffect } from 'react';
import { Save, FileText, Send } from 'lucide-react';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/utils';

type Template = {
  id: number;
  name: string;
  language: string;
  subject: string;
  body: string;
  updated_at: string;
};

const PLACEHOLDERS = [
  { tag: '{{client_name}}', desc: "Client's name" },
  { tag: '{{transaction_table}}', desc: 'Auto-generated table of missing transactions' },
  { tag: '{{total_amount}}', desc: 'Total amount of WHT being chased (formatted)' },
  { tag: '{{mailbit_tax_id}}', desc: "MailBIT's tax ID" },
  { tag: '{{follow_up_count}}', desc: 'Follow-up email count (follow_up template only)' },
];

export function Templates() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [activeName, setActiveName] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [originalSubject, setOriginalSubject] = useState('');
  const [originalBody, setOriginalBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    api.emailTemplates()
      .then((res) => {
        const list = res.templates as unknown as Template[];
        setTemplates(list);
        if (list.length > 0) selectTemplate(list[0]);
      })
      .finally(() => setLoading(false));

    api.me()
      .then((res) => setTestEmail(res.user.email))
      .catch(() => {});
  }, []);

  const selectTemplate = (t: Template) => {
    setActiveName(t.name);
    setSubject(t.subject);
    setBody(t.body);
    setOriginalSubject(t.subject);
    setOriginalBody(t.body);
    setMessage(null);
  };

  const dirty = subject !== originalSubject || body !== originalBody;

  const handleSave = async () => {
    if (!activeName || !dirty) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await api.updateEmailTemplate(activeName, { subject, body });
      const updated = res.template as unknown as Template;
      setTemplates((ts) => ts.map((t) => (t.name === activeName ? updated : t)));
      setOriginalSubject(updated.subject);
      setOriginalBody(updated.body);
      setMessage({ type: 'success', text: 'Template saved. New emails will use this version.' });
    } catch (err) {
      setMessage({ type: 'error', text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    setSubject(originalSubject);
    setBody(originalBody);
    setMessage(null);
  };

  const handleSendTest = async () => {
    if (!activeName || !testEmail.trim()) return;
    setTesting(true);
    setMessage(null);
    try {
      // Send the CURRENT (possibly unsaved) edits as the test, so staff can
      // verify a draft before saving it as the active template.
      const res = await api.testEmail({
        to: testEmail.trim(),
        templateName: activeName,
        customSubject: dirty ? subject : undefined,
        customBody: dirty ? body : undefined,
      });
      setMessage({
        type: 'success',
        text: `Test email sent to ${res.sentTo}${dirty ? ' (using unsaved edits)' : ''}. Check your inbox.`,
      });
    } catch (err) {
      setMessage({ type: 'error', text: `Test send failed: ${(err as Error).message}` });
    } finally {
      setTesting(false);
    }
  };

  const insertPlaceholder = (tag: string) => {
    setBody((b) => b + (b.endsWith('\n') || b === '' ? '' : ' ') + tag);
  };

  const activeTemplate = templates.find((t) => t.name === activeName);

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Loading templates...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText size={22} /> Email Templates
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Edit the subject and body used for all certificate request and follow-up emails.
          Changes apply to every future email — no per-message editing needed.
        </p>
      </div>

      {message && (
        <div
          className={`border rounded-lg p-3 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Template list */}
        <div className="bg-white rounded-lg border p-2 h-fit">
          {templates.map((t) => (
            <button
              key={t.name}
              onClick={() => {
                if (dirty && !confirm('Discard unsaved changes?')) return;
                selectTemplate(t);
              }}
              className={`w-full text-left px-3 py-2 rounded text-sm ${
                activeName === t.name
                  ? 'bg-blue-50 text-blue-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="font-medium">{t.name}</div>
              <div className="text-xs text-gray-400 mt-0.5">{t.language.toUpperCase()}</div>
            </button>
          ))}
          {templates.length === 0 && (
            <p className="text-sm text-gray-400 px-3 py-4">No templates found</p>
          )}
        </div>

        {/* Editor */}
        {activeTemplate && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg border">
              <div className="p-4 border-b flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-semibold">{activeTemplate.name}</h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Last updated {formatDateTime(activeTemplate.updated_at)}
                  </p>
                </div>
                <div className="flex gap-2">
                  {dirty && (
                    <button
                      onClick={handleRevert}
                      disabled={saving}
                      className="px-3 py-1.5 border rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Revert
                    </button>
                  )}
                  <button
                    onClick={handleSave}
                    disabled={!dirty || saving}
                    className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Save size={14} /> {saving ? 'Saving...' : 'Save Template'}
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase">Subject</label>
                  <input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm mt-1 font-medium"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Body</label>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm mt-1 font-mono"
                    rows={22}
                    spellCheck={false}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Pipe-style table rows (| col | col |) become HTML tables when sent.
                  </p>
                </div>
              </div>

              <div className="p-4 border-t bg-gray-50/50">
                <div className="flex items-end gap-3 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <label className="text-xs text-gray-500 uppercase">Send test to</label>
                    <input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full border rounded px-3 py-2 text-sm mt-1"
                    />
                  </div>
                  <button
                    onClick={handleSendTest}
                    disabled={testing || !testEmail.trim()}
                    className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded text-sm hover:bg-blue-50 disabled:opacity-50"
                  >
                    <Send size={14} /> {testing ? 'Sending...' : 'Send Test Email'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Test email is rendered with sample data, prefixed with <span className="font-mono">[TEST]</span>,
                  and not logged. {dirty && <span className="text-amber-600">Will use your unsaved edits.</span>}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg border p-4">
              <h4 className="text-sm font-semibold mb-2">Available placeholders</h4>
              <p className="text-xs text-gray-500 mb-3">
                These get replaced with real values when the email is sent. Click to append to body.
              </p>
              <div className="space-y-1.5">
                {PLACEHOLDERS.map((p) => (
                  <button
                    key={p.tag}
                    onClick={() => insertPlaceholder(p.tag)}
                    className="flex items-start gap-3 w-full text-left px-2 py-1.5 rounded hover:bg-gray-50"
                  >
                    <code className="text-xs bg-gray-100 text-blue-700 px-2 py-0.5 rounded font-mono shrink-0">
                      {p.tag}
                    </code>
                    <span className="text-xs text-gray-600">{p.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
